import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { BuiltUpAreaLand } from "./builtUpAreaLand.types";

@Injectable({
    providedIn: 'root'
})
export class BuiltUpAreaLandService {
    // Private
    private _builtUpAreaLand: BehaviorSubject<BuiltUpAreaLand | null> = new BehaviorSubject(null);
    private _builtUpAreaLands: BehaviorSubject<BuiltUpAreaLand[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for builtUpArea
     */
    get builtUpAreaLand$(): Observable<BuiltUpAreaLand> {
        return this._builtUpAreaLand.asObservable();
    }

    /**
     * Getter for builtUpAreas
     */
    get builtUpAreaLands$(): Observable<BuiltUpAreaLand[]> {
        return this._builtUpAreaLands.asObservable();
    }

    /**
     * Get builtUpAreas
     */
    getBuiltUpAreaLands(reportId: number): Observable<BaseRespone<BuiltUpAreaLand[]>> {
        return this._httpClient.get<BaseRespone<BuiltUpAreaLand[]>>(`${environment.baseUrl}/Report/GetBuiltUpAreaBreakUpLandslist/${reportId}`).pipe(
            tap((response) => {
                this._builtUpAreaLands.next(response.result);
            })
        );
    }

    /**
      * Create builtUpArea
      */
    createBuiltUpAreaLand(builtUpArea: BuiltUpAreaLand): Observable<BaseRespone<BuiltUpAreaLand>> {
        return this.builtUpAreaLands$.pipe(
            take(1),
            switchMap(builtUpAreaLands => this._httpClient.post<BaseRespone<BuiltUpAreaLand>>(`${environment.baseUrl}/Report/AddBuiltUpAreaBreakUpLands`, builtUpArea)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => {
                        // Return the new product
                        if (response.isSuccess) {
                            this._builtUpAreaLands.next([response.result, ...builtUpAreaLands]);

                        }
                        return response;
                    })
                ))
        );
    }

    /**
     * Get board
     *
     * @param id
     */
    getBuiltUpAreaLand(id: number): Observable<BaseRespone<BuiltUpAreaLand>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetBuiltUpAreaBreakUpLands/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._builtUpAreaLand.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getBuiltUpAreaLandById(id: number): Observable<BuiltUpAreaLand> {
        return this._builtUpAreaLands.pipe(
            take(1),
            map((builtUpAreaLands) => {

                // Find the contact
                const builtUpAreaLand = builtUpAreaLands.find(item => item.id === id) || null;

                // Update the builtUpAreaLand
                this._builtUpAreaLand.next(builtUpAreaLand);

                // Return the builtUpAreaLand
                return builtUpAreaLand;
            }),
            switchMap((builtUpAreaLand) => {

                if (!builtUpAreaLand) {
                    return throwError('Could not found builtUpAreaLand with id of ' + id + '!');
                }

                return of(builtUpAreaLand);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateBuiltUpAreaLand(builtUpAreaLand: BuiltUpAreaLand): Observable<BaseRespone<BuiltUpAreaLand>> {
        return this.builtUpAreaLands$.pipe(
            take(1),
            switchMap(builtUpAreaLands => this._httpClient.put<BaseRespone<BuiltUpAreaLand>>(`${environment.baseUrl}/Report/UpdateBuiltUpAreaBreakUpLands`, builtUpAreaLand).pipe(
                map((builtUpAreaLandCard) => {
                    const index = builtUpAreaLands.findIndex(item => item.id === builtUpAreaLand.id);

                    // Update the product
                    if (builtUpAreaLandCard.isSuccess) {
                        builtUpAreaLands[index] = builtUpAreaLandCard.result;

                        // Update the products
                        this._builtUpAreaLands.next(builtUpAreaLands);

                        this._builtUpAreaLand.next(builtUpAreaLandCard.result);
                    }
                    // Return the updated card
                    return builtUpAreaLandCard;
                }), catchError((error) => {
                    return of(error.error);
                })
            ))
        );
    }

    deleteBuiltUpAreaLand(id: number): Observable<BaseRespone<boolean>> {
        return this.builtUpAreaLands$.pipe(
            take(1),
            switchMap(builtUpAreaLands => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteBuiltUpAreaBreakUpLands/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = builtUpAreaLands.findIndex(item => item.id === id);

                    // Delete the product
                    builtUpAreaLands.splice(index, 1);

                    // Update the products
                    this._builtUpAreaLands.next(builtUpAreaLands);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }


    deleteAllBuiltUpAreaLand(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this.builtUpAreaLands$.pipe(
            take(1),
            switchMap(builtUpAreaLand => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteAllBuiltUpAreaBreakUpsLand`, {
                body: requestBody
            }).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    ids.forEach(id => {
                        // Find the index of the deleted product
                        const index = builtUpAreaLand.findIndex(item => item.id === id);
                        // Delete the product
                        builtUpAreaLand.splice(index, 1);
                    });
                    // Update the products
                    this._builtUpAreaLands.next(builtUpAreaLand);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );

    }

}
